import { Helmet } from 'react-helmet';

import { buildLocalizedAlternateUrls, resolveCurrentPathLocale, resolveLocalizedCanonicalUrl } from '@/utils/localePublicUrl';

const DEFAULT_OG_IMAGE = 'https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/b640dc78-9146-4a0e-5d34-1d25ced2ce00/public';

function resolveCanonicalUrl({ canonicalUrl, canonicalPath }) {
  return resolveLocalizedCanonicalUrl({ canonicalUrl, canonicalPath });
}

function buildDefaultStructuredData({
  title,
  description,
  canonicalUrl,
  image,
  pageType,
  breadcrumbItems,
}) {
  const pageId = `${canonicalUrl}#webpage`;
  const pageNode = {
    '@type': pageType === 'CollectionPage' ? 'CollectionPage' : 'WebPage',
    '@id': pageId,
    url: canonicalUrl,
    name: title,
    description,
    primaryImageOfPage: image,
  };

  if (!Array.isArray(breadcrumbItems) || breadcrumbItems.length === 0) {
    return {
      '@context': 'https://schema.org',
      ...pageNode,
    };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        ...pageNode,
        breadcrumb: {
          '@id': `${canonicalUrl}#breadcrumb`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: breadcrumbItems.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      },
    ],
  };
}

export default function StaticSeoHead({
  title,
  description,
  canonicalUrl,
  canonicalPath,
  robots = 'index,follow,max-image-preview:large',
  ogType = 'website',
  image = DEFAULT_OG_IMAGE,
  pageType = 'WebPage',
  breadcrumbItems = [],
  structuredData = undefined,
}) {
  const resolvedCanonicalUrl = resolveCanonicalUrl({ canonicalUrl, canonicalPath });
  const resolvedLocale = resolveCurrentPathLocale();
  const alternateUrls = buildLocalizedAlternateUrls({ canonicalUrl, canonicalPath, locale: resolvedLocale });
  const resolvedStructuredData = structuredData === null
    ? null
    : (structuredData || buildDefaultStructuredData({
      title,
      description,
      canonicalUrl: resolvedCanonicalUrl,
      image,
      pageType,
      breadcrumbItems,
    }));

  return (
    <Helmet>
      <html lang={resolvedLocale} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={resolvedCanonicalUrl} />
      {alternateUrls.map((alternate) => (
        <link
          key={alternate.hrefLang}
          rel="alternate"
          hrefLang={alternate.hrefLang}
          href={alternate.href}
        />
      ))}
      <meta property="og:site_name" content="Suarte" />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={resolvedCanonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {resolvedStructuredData ? (
        <script type="application/ld+json">
          {JSON.stringify(resolvedStructuredData)}
        </script>
      ) : null}
    </Helmet>
  );
}
